import React from 'react';
import { motion } from 'framer-motion';
import { Target, Lightbulb, Settings, TrendingUp, ScanLine } from 'lucide-react';
export function AboutPage() {
  return <div className="min-h-screen bg-sandev-bg" data-id="element-90">
      {/* Header */}
      <section className="pt-24 pb-16 bg-sandev-card border-b border-sandev-border" data-id="element-91">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-92">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-3xl" data-id="element-93">
            <h1 className="text-4xl md:text-5xl font-bold text-sandev-text mb-6" data-id="element-94">
              About SanDev Industries
            </h1>
            <p className="text-xl text-sandev-muted leading-relaxed" data-id="element-95">
              We are bridging the gap between concept and reality. SanDev
              Industries was founded on a simple premise: industrial-quality
              prototyping shouldn't require industrial-scale budgets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20" data-id="element-96">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-97">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-98">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-99">
              <h2 className="text-3xl font-bold text-sandev-text mb-6 flex items-center gap-3" data-id="element-100">
                <Lightbulb className="text-sandev-accent" data-id="element-101" />
                The Business Idea
              </h2>
              <div className="space-y-6 text-sandev-muted text-lg leading-relaxed" data-id="element-102">
                <p data-id="element-103">
                  In traditional manufacturing, creating a single prototype
                  often involves expensive tooling, CNC machining setup costs,
                  or minimum order quantities. This creates a massive barrier to
                  entry for rapid iteration.
                </p>
                <p data-id="element-104">
                  SanDev Industries leverages advanced additive manufacturing
                  (3D printing) and 3D scanning to bypass these traditional
                  constraints. By utilizing Fused Deposition Modeling (FDM),
                  high-detail Resin printing, and precision 3D scanning, we
                  offer a cheaper, faster alternative that doesn't compromise on
                  quality.
                </p>
                <p data-id="element-105">
                  Our service is designed specifically for industrial and
                  manufacturing companies who need to hold a physical part in
                  their hands, test its fit, or verify its form before
                  committing thousands of dollars to mass production tooling.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-sandev-card border border-sandev-border rounded-2xl p-8 shadow-2xl relative overflow-hidden" data-id="element-106">
              <div className="absolute top-0 right-0 w-64 h-64 bg-sandev-accent/5 rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/2" data-id="element-107"></div>
              <div className="aspect-video bg-sandev-bg rounded-xl border border-sandev-border flex items-center justify-center mb-8 overflow-hidden relative" data-id="element-108">
                <img src="https://cdn.magicpatterns.com/uploads/rmRVKZFYhXZrX1RFCfWxiS/SanDev_Logo_v5.png" alt="SanDev Logo" className="w-full h-full object-cover ml-[500px] mr-[500px]" data-id="element-109" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-110">
                <div className="bg-sandev-bg p-4 rounded-lg border border-sandev-border" data-id="element-111">
                  <div className="text-2xl font-bold text-sandev-accent mb-1" data-id="element-112">
                    10x
                  </div>
                  <div className="text-sm text-sandev-muted" data-id="element-113">
                    Faster than traditional tooling
                  </div>
                </div>
                <div className="bg-sandev-bg p-4 rounded-lg border border-sandev-border" data-id="element-114">
                  <div className="text-2xl font-bold text-sandev-accent mb-1" data-id="element-115">
                    -80%
                  </div>
                  <div className="text-sm text-sandev-muted" data-id="element-116">
                    Average cost reduction
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24" data-id="element-117">
            <div className="bg-sandev-card border border-sandev-border rounded-xl p-8" data-id="element-118">
              <Target className="text-sandev-accent w-10 h-10 mb-6" data-id="element-119" />
              <h3 className="text-2xl font-bold text-sandev-text mb-4" data-id="element-120">
                Our Mission
              </h3>
              <p className="text-sandev-muted leading-relaxed" data-id="element-121">
                To empower engineers, designers, and manufacturers to iterate
                freely by providing accessible, high-quality, and rapid
                prototyping services. We believe that the ability to fail fast
                and iterate cheaply is the key to superior final products.
              </p>
            </div>
            <div className="bg-sandev-card border border-sandev-border rounded-xl p-8" data-id="element-122">
              <TrendingUp className="text-sandev-accent w-10 h-10 mb-6" data-id="element-123" />
              <h3 className="text-2xl font-bold text-sandev-text mb-4" data-id="element-124">
                Our Vision
              </h3>
              <p className="text-sandev-muted leading-relaxed" data-id="element-125">
                To become the go-to rapid prototyping partner for the
                manufacturing sector, continuously expanding our material
                capabilities and technological offerings to bridge the gap
                between digital design and physical reality.
              </p>
            </div>
          </div>

          {/* Capabilities */}
          <div data-id="element-126">
            <h2 className="text-3xl font-bold text-sandev-text mb-10 text-center" data-id="element-127">
              Technical Capabilities
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-128">
              <div className="bg-sandev-bg border border-sandev-border rounded-xl p-6" data-id="element-129">
                <Settings className="text-sandev-accent mb-4" size={24} data-id="element-130" />
                <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-131">
                  Industrial FDM
                </h4>
                <p className="text-sm text-sandev-muted" data-id="element-132">
                  High-temperature capable machines allowing for
                  engineering-grade materials like Nylon, Polycarbonate, and
                  Carbon Fiber composites. Large build volumes for substantial
                  parts.
                </p>
              </div>
              <div className="bg-sandev-bg border border-sandev-border rounded-xl p-6" data-id="element-133">
                <Settings className="text-sandev-accent mb-4" size={24} data-id="element-134" />
                <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-135">
                  MSLA Resin
                </h4>
                <p className="text-sm text-sandev-muted" data-id="element-136">
                  High-resolution monochrome screens delivering XY resolutions
                  down to 22 microns. Perfect for intricate details, smooth
                  surfaces, and specialized resin applications.
                </p>
              </div>
              <div className="bg-sandev-bg border border-sandev-border rounded-xl p-6" data-id="element-137">
                <Settings className="text-sandev-accent mb-4" size={24} data-id="element-138" />
                <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-139">
                  Post-Processing
                </h4>
                <p className="text-sm text-sandev-muted" data-id="element-140">
                  Complete finishing services including support removal, UV
                  curing, sanding, priming, and threaded insert installation for
                  functional assemblies.
                </p>
              </div>
              <div className="bg-sandev-bg border border-sandev-border rounded-xl p-6" data-id="element-141">
                <ScanLine className="text-sandev-accent mb-4" size={24} data-id="element-142" />
                <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-143">
                  3D Scanning
                </h4>
                <p className="text-sm text-sandev-muted" data-id="element-144">
                  High-precision 3D scanning for reverse engineering existing
                  parts, quality inspection against CAD models, and creating
                  digital twins of physical objects.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}